import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './single-flight';
import type { QueryKey } from '@tanstack/react-query';
import { useDataSource, type DataSource } from './data-source';
import { trackAction } from '../analytics/mutation-events';
import type { JobState } from './types';

/** Query keys; the live bridge maps (entity, id) onto these (src/live/useLiveUpdates.ts). */
export const keys = {
  me: (): QueryKey => ['me'],
  fleet: (tenant: string): QueryKey => ['fleet', tenant],
  job: (tenant: string, jobId: string): QueryKey => ['job', tenant, jobId],
  exceptions: (tenant: string, jobId: string): QueryKey => ['exceptions', tenant, jobId],
};

export function fleetQuery(ds: DataSource, tenant: string) {
  return queryOptions({
    queryKey: keys.fleet(tenant),
    queryFn: () => ds.fleet.getFleet(tenant),
    staleTime: 10_000,
    // The stream is the way the board moves; this is the floor under it, so a dead stream reads
    // as a slow day rather than a frozen one (settings review, row 44).
    refetchInterval: 30_000,
  });
}

export function jobQuery(ds: DataSource, tenant: string, jobId: string) {
  return queryOptions({
    queryKey: keys.job(tenant, jobId),
    queryFn: () => ds.fleet.getJob(tenant, jobId),
    staleTime: 10_000,
  });
}

export const useFleet = (tenant: string) => useQuery(fleetQuery(useDataSource(), tenant));
export const useJob = (tenant: string, jobId: string) =>
  useQuery(jobQuery(useDataSource(), tenant, jobId));

export function useExceptions(tenant: string, jobId: string) {
  const ds = useDataSource();
  return useQuery({
    queryKey: keys.exceptions(tenant, jobId),
    queryFn: () => ds.fleet.listExceptions(tenant, jobId),
  });
}

/** Mutations set the returned entity into the cache and invalidate the fleet. */
export function useSetJobState(tenant: string, jobId: string) {
  const ds = useDataSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (state: JobState) => ds.fleet.setJobState(tenant, jobId, state),
    onSuccess: (job, state) => {
      trackAction('job.state_changed', { state });
      if (state === 'archived') trackAction('job.archived');
      qc.setQueryData(keys.job(tenant, jobId), job);
      void qc.invalidateQueries({ queryKey: keys.fleet(tenant) });
    },
  });
}
